"use client";

import { c as _c } from "react/compiler-runtime";
import { toast as sonnerToast } from "sonner";
import { useEffect, useRef } from "react";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useInPersonLocked } from "@/hooks/use-in-person-form-lock";
import { nextUpTypes, usePatientStatus } from "@/hooks/use-patient-status";
import { calculateRelativeDuration } from "@/lib/format-data";
import { useToast } from "@/lib/use-toast";
import { AppointmentChecklistType } from "@/types/enums/appointment";
import { CreateNoteState } from "@/types/enums/note-state";
import { useCurrentTime } from "./use-current-time";
import { useNotificationsDrawerStore } from "@/stores/notifications-drawer-store";
import { usePatientStatusDrawerStore } from "@/stores/patient-status-drawer-store";
import { isPatientStatusChecklistVisible, patientStatusVisibilityScope, usePatientStatusStore } from "@/stores/patient-status-store";
import { useScribeStore } from "@/stores/scribe-store";
export function usePatientStatusOverdueToasts() {
  const $ = _c(61);
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const t0 = tenant?.subDomain;
  const t1 = currentUser?.id;
  let t2;
  if ($[0] !== t0 || $[1] !== t1) {
    t2 = patientStatusVisibilityScope(t0, t1);
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const visibilityScope = t2;
  const waitingEvents = usePatientStatus();
  const {
    closeDrawer: closeNotificationsPanel
  } = useNotificationsDrawerStore();
  const {
    openDrawer: openPatientStatusPanel,
    isOpen: isPatientStatusPanelOpen
  } = usePatientStatusDrawerStore();
  const {
    toast
  } = useToast();
  const recordingNoteState = useScribeStore(_temp);
  const isInPersonLocked = useInPersonLocked();
  const isSuppressed = recordingNoteState === CreateNoteState.RECORDING || recordingNoteState === CreateNoteState.PAUSED || isInPersonLocked;
  const setOverdueToastState = usePatientStatusStore(_temp2);
  const dismissAllActiveToasts = usePatientStatusStore(_temp3);
  const cleanupOverdueToasts = usePatientStatusStore(_temp4);
  let t3;
  if ($[3] !== tenant?.enabledModules) {
    t3 = tenant?.enabledModules?.includes("CALENDAR_PATIENT_STATUS");
    $[3] = tenant?.enabledModules;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isPatientStatusEnabled = t3;
  const showOverdueToasts = currentUser?.showOverdueToasts ?? false;
  const checklistVisibility = usePatientStatusStore(_temp5);
  const checklistVisibilityDefaults = usePatientStatusStore(_temp6);
  const showOverdueToastsRef = useRef(showOverdueToasts);
  let t4;
  let t5;
  if ($[5] !== showOverdueToasts) {
    t4 = () => {
      showOverdueToastsRef.current = showOverdueToasts;
    };
    t5 = [showOverdueToasts];
    $[5] = showOverdueToasts;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  const isSuppressedRef = useRef(isSuppressed);
  let t6;
  let t7;
  if ($[8] !== isSuppressed) {
    t6 = () => {
      isSuppressedRef.current = isSuppressed;
    };
    t7 = [isSuppressed];
    $[8] = isSuppressed;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  const activeScopeRef = useRef(null);
  let t8;
  let t9;
  if ($[11] !== visibilityScope) {
    t8 = () => {
      activeScopeRef.current = visibilityScope;
      if (!visibilityScope) {
        return;
      }
      return () => {
        if (activeScopeRef.current === visibilityScope) {
          activeScopeRef.current = null;
        }
        const overdueToasts = usePatientStatusStore.getState().overdueToasts[visibilityScope] ?? {};
        for (const [key, state] of Object.entries(overdueToasts)) {
          if (state === "active") {
            sonnerToast.dismiss(key);
          }
        }
      };
    };
    t9 = [visibilityScope];
    $[11] = visibilityScope;
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[14] !== showOverdueToasts || $[15] !== visibilityScope) {
    t10 = () => {
      if (!showOverdueToasts && visibilityScope) {
        const overdueToasts_0 = usePatientStatusStore.getState().overdueToasts[visibilityScope] ?? {};
        for (const [key_0, state_0] of Object.entries(overdueToasts_0)) {
          if (state_0 === "active") {
            sonnerToast.dismiss(key_0);
          }
        }
      }
    };
    t11 = [showOverdueToasts, visibilityScope];
    $[14] = showOverdueToasts;
    $[15] = visibilityScope;
    $[16] = t10;
    $[17] = t11;
  } else {
    t10 = $[16];
    t11 = $[17];
  }
  useEffect(t10, t11);
  const prevPanelOpen = useRef(isPatientStatusPanelOpen);
  let t12;
  let t13;
  if ($[18] !== dismissAllActiveToasts || $[19] !== isPatientStatusPanelOpen || $[20] !== visibilityScope) {
    t12 = () => {
      if (visibilityScope && isPatientStatusPanelOpen && !prevPanelOpen.current) {
        const activeKeys = dismissAllActiveToasts(visibilityScope);
        for (const key_1 of activeKeys) {
          sonnerToast.dismiss(key_1);
        }
      }
      prevPanelOpen.current = isPatientStatusPanelOpen;
    };
    t13 = [isPatientStatusPanelOpen, dismissAllActiveToasts, visibilityScope];
    $[18] = dismissAllActiveToasts;
    $[19] = isPatientStatusPanelOpen;
    $[20] = visibilityScope;
    $[21] = t12;
    $[22] = t13;
  } else {
    t12 = $[21];
    t13 = $[22];
  }
  useEffect(t12, t13);
  const prevSuppressed = useRef(isSuppressed);
  let t14;
  let t15;
  if ($[23] !== isSuppressed || $[24] !== visibilityScope) {
    t14 = () => {
      if (visibilityScope && isSuppressed && !prevSuppressed.current) {
        const overdueToasts_1 = usePatientStatusStore.getState().overdueToasts[visibilityScope] ?? {};
        for (const [key_2, state_1] of Object.entries(overdueToasts_1)) {
          if (state_1 === "active") {
            sonnerToast.dismiss(key_2);
          }
        }
      }
      prevSuppressed.current = isSuppressed;
    };
    t15 = [isSuppressed, visibilityScope];
    $[23] = isSuppressed;
    $[24] = visibilityScope;
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[27] !== cleanupOverdueToasts || $[28] !== visibilityScope || $[29] !== waitingEvents.allGroupIndex || $[30] !== waitingEvents.isLoading) {
    t16 = () => {
      if (waitingEvents.isLoading || !visibilityScope) {
        return;
      }
      const activeToastKeys = waitingEvents.allGroupIndex.flatMap(_temp8);
      const removedKeys = cleanupOverdueToasts(visibilityScope, activeToastKeys);
      for (const key_3 of removedKeys) {
        sonnerToast.dismiss(key_3);
      }
    };
    t17 = [waitingEvents.allGroupIndex, waitingEvents.isLoading, cleanupOverdueToasts, visibilityScope];
    $[27] = cleanupOverdueToasts;
    $[28] = visibilityScope;
    $[29] = waitingEvents.allGroupIndex;
    $[30] = waitingEvents.isLoading;
    $[31] = t16;
    $[32] = t17;
  } else {
    t16 = $[31];
    t17 = $[32];
  }
  useEffect(t16, t17);
  const currentTime = useCurrentTime(60000);
  let t18;
  if ($[33] !== checklistVisibility || $[34] !== checklistVisibilityDefaults || $[35] !== closeNotificationsPanel || $[36] !== isPatientStatusEnabled || $[37] !== isPatientStatusPanelOpen || $[38] !== isSuppressed || $[39] !== openPatientStatusPanel || $[40] !== setOverdueToastState || $[41] !== showOverdueToasts || $[42] !== toast || $[43] !== visibilityScope || $[44] !== waitingEvents) {
    t18 = () => {
      if (!visibilityScope || !isPatientStatusEnabled || !showOverdueToasts || isSuppressed) {
        return;
      }
      const overdueToasts_2 = usePatientStatusStore.getState().overdueToasts[visibilityScope] ?? {};
      for (const group of waitingEvents.allGroupIndex) {
        for (const row of group.rows) {
          if (!waitingEvents.isOverdue(row)) {
            continue;
          }
          const nextUpType_0 = row.nextUpItem?.type as AppointmentChecklistType;
          const key_4 = row.id ?? `${row.event.id}:${nextUpType_0}`;
          const existing = overdueToasts_2[key_4];
          if (existing === "dismissed") {
            continue;
          }
          const checklistName = row.nextUpItem?.name;
          if (!checklistName || !isPatientStatusChecklistVisible(checklistVisibility, checklistVisibilityDefaults, visibilityScope, checklistName)) {
            setOverdueToastState(visibilityScope, key_4, "dismissed");
            continue;
          }
          const patientName = `${row.patient.firstName} ${row.patient.lastName}`;
          const statusLabel = nextUpTypes[nextUpType_0]?.label ?? checklistName;
          const waitTime = row.lastCheckedItem?.completedDate ? calculateRelativeDuration(row.lastCheckedItem.completedDate) : "";
          const description = `${statusLabel}${waitTime ? ` — ${waitTime}` : ""}`;
          if (!existing) {
            setOverdueToastState(visibilityScope, key_4, "active");
          }
          if (isPatientStatusPanelOpen) {
            setOverdueToastState(visibilityScope, key_4, "dismissed");
            continue;
          }
          toast({
            variant: "warning",
            title: `${patientName} has been waiting`,
            description,
            action: {
              label: "View",
              altText: "Open patient status panel",
              onClick: () => {
                setOverdueToastState(visibilityScope, key_4, "dismissed");
                closeNotificationsPanel();
                openPatientStatusPanel();
              }
            },
            id: key_4,
            duration: Infinity,
            onDismiss: () => {
              if (activeScopeRef.current === visibilityScope && showOverdueToastsRef.current && !isSuppressedRef.current) {
                setOverdueToastState(visibilityScope, key_4, "dismissed");
              }
            }
          });
        }
      }
    };
    $[33] = checklistVisibility;
    $[34] = checklistVisibilityDefaults;
    $[35] = closeNotificationsPanel;
    $[36] = isPatientStatusEnabled;
    $[37] = isPatientStatusPanelOpen;
    $[38] = isSuppressed;
    $[39] = openPatientStatusPanel;
    $[40] = setOverdueToastState;
    $[41] = showOverdueToasts;
    $[42] = toast;
    $[43] = visibilityScope;
    $[44] = waitingEvents;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== checklistVisibility || $[47] !== checklistVisibilityDefaults || $[48] !== closeNotificationsPanel || $[49] !== currentTime || $[50] !== isPatientStatusEnabled || $[51] !== isPatientStatusPanelOpen || $[52] !== isSuppressed || $[53] !== openPatientStatusPanel || $[54] !== setOverdueToastState || $[55] !== showOverdueToasts || $[56] !== toast || $[57] !== visibilityScope || $[58] !== waitingEvents.allGroupIndex || $[59] !== waitingEvents.isOverdue) {
    t19 = [waitingEvents.allGroupIndex, waitingEvents.isOverdue, isPatientStatusEnabled, isSuppressed, showOverdueToasts, checklistVisibility, checklistVisibilityDefaults, visibilityScope, isPatientStatusPanelOpen, toast, setOverdueToastState, closeNotificationsPanel, openPatientStatusPanel, currentTime];
    $[46] = checklistVisibility;
    $[47] = checklistVisibilityDefaults;
    $[48] = closeNotificationsPanel;
    $[49] = currentTime;
    $[50] = isPatientStatusEnabled;
    $[51] = isPatientStatusPanelOpen;
    $[52] = isSuppressed;
    $[53] = openPatientStatusPanel;
    $[54] = setOverdueToastState;
    $[55] = showOverdueToasts;
    $[56] = toast;
    $[57] = visibilityScope;
    $[58] = waitingEvents.allGroupIndex;
    $[59] = waitingEvents.isOverdue;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  useEffect(t18, t19);
}
function _temp8(g) {
  return g.rows.map(_temp7);
}
function _temp7(r) {
  const nextUpType = r.nextUpItem?.type as AppointmentChecklistType;
  return r.id ?? `${r.event.id}:${nextUpType}`;
}
function _temp6(s_4) {
  return s_4.checklistVisibilityDefaults;
}
function _temp5(s_3) {
  return s_3.checklistVisibility;
}
function _temp4(s_2) {
  return s_2.cleanupOverdueToasts;
}
function _temp3(s_1) {
  return s_1.dismissAllActiveToasts;
}
function _temp2(s_0) {
  return s_0.setOverdueToastState;
}
function _temp(s) {
  return s.recordingNoteState;
}